import React, { useEffect, useMemo, useRef, useState } from "react";
import { RecBar } from "../materials/MaterialRecordParts";
import { can, CAPABILITY } from "../../helpers/permissions";
import {
  approveAllRecommendations,
  approveRecommendation,
  captureConversation,
  dismissRecommendation,
  isConversationsUnavailableError,
  loadRecommendedTasks,
} from "../../projectConversations/recommendedTasks";
import { useDictation } from "../../speech/useDictation";
import { SpeechDebugPanel } from "../../speech/SpeechDebugPanel";
import "./projectConversations.css";

function todayIso() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export function ProjectConversationsScreen({
  actor,
  projectId,
  projectName,
  clientId = null,
  locations = [],
  onTasksCreated,
  onBack,
}) {
  const [transcript, setTranscript] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState([]);
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState("");
  const [draft, setDraft] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const usedSpeech = useRef(false);
  const transcriptRef = useRef("");
  transcriptRef.current = transcript;
  // Shared dictation lifecycle (same model as Build Tasks with Ashley).
  const dictation = useDictation({
    getText: () => transcriptRef.current,
    onText: setTranscript,
    onError: () => setError("Transcription stopped. The text captured so far is kept. You can edit it and analyze."),
  });
  const { recording } = dictation;
  const mayApprove = can(actor, CAPABILITY.PROJECT_TASK_MANAGE);
  const proposed = useMemo(() => rows.filter((row) => row.status === "proposed"), [rows]);

  useEffect(() => {
    let cancelled = false;
    loadRecommendedTasks(actor, projectId)
      .then((loaded) => {
        if (!cancelled) setRows(loaded);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isConversationsUnavailableError(err)) setUnavailable(true);
        else setError(err?.message || "Unable to load recommendations.");
      });
    return () => {
      cancelled = true;
    };
  }, [actor, projectId]);

  function startRecording() {
    setError("");
    if (!dictation.start()) {
      setError("This browser has no speech transcription. Type the transcript below, then analyze it.");
      return;
    }
    usedSpeech.current = true;
  }

  function stopRecording() {
    dictation.stop();
  }

  async function analyze() {
    setBusy(true);
    setError("");
    setNotes([]);
    try {
      const result = await captureConversation(actor, {
        projectId,
        clientId,
        transcript,
        locations,
        referenceDate: todayIso(),
        captureSource: usedSpeech.current ? "microphone" : "external",
      });
      setNotes(result.notes || []);
      setRows((current) => [...(result.recommendations || []), ...current]);
      setTranscript("");
      usedSpeech.current = false;
    } catch (err) {
      if (isConversationsUnavailableError(err)) setUnavailable(true);
      else setError(err?.message || "Unable to analyze that conversation.");
    } finally {
      setBusy(false);
    }
  }

  async function approve(row, edits) {
    setBusy(true);
    setError("");
    try {
      const { task } = await approveRecommendation(actor, row, edits);
      setRows((current) =>
        current.map((item) =>
          item.recommendation_id === row.recommendation_id
            ? { ...item, status: "approved", project_task_id: task.project_task_id, ...editsToRow(edits) }
            : item,
        ),
      );
      setEditingId("");
      setDraft(null);
      onTasksCreated?.();
    } catch (err) {
      setError(err?.message || "Unable to approve that task.");
    } finally {
      setBusy(false);
    }
  }

  async function dismiss(row) {
    setBusy(true);
    setError("");
    try {
      await dismissRecommendation(actor, row);
      setRows((current) =>
        current.map((item) =>
          item.recommendation_id === row.recommendation_id ? { ...item, status: "dismissed" } : item,
        ),
      );
    } catch (err) {
      setError(err?.message || "Unable to dismiss that recommendation.");
    } finally {
      setBusy(false);
    }
  }

  async function approveAll() {
    setBusy(true);
    setError("");
    try {
      const approved = await approveAllRecommendations(actor, proposed);
      const done = new Set(approved.map((item) => item.recommendation.recommendation_id));
      setRows((current) =>
        current.map((item) => (done.has(item.recommendation_id) ? { ...item, status: "approved" } : item)),
      );
      if (approved.length) onTasksCreated?.();
    } catch (err) {
      setError(err?.message || "Unable to approve every recommendation.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen nx-screen rec-page" data-testid="project-conversations">
      <RecBar title="Conversations" onBack={onBack} />
      <div className="rec-body">
        <p className="cv-lead">
          {projectName ? `${projectName}. ` : ""}
          Record what was said. CrewPilot proposes tasks. Nothing is added to the project until you approve it.
        </p>
        {unavailable ? (
          <div className="rec-banner" role="alert">
            Conversation recommendations are not available in this database yet.
          </div>
        ) : null}
        {error ? (
          <div className="rec-banner" role="alert">
            {error}
          </div>
        ) : null}
        <section className="rec-card cv-card">
          <div className="cv-actions">
            {recording ? (
              <button type="button" className="cv-btn" onClick={stopRecording} data-testid="conversation-stop">
                Stop
              </button>
            ) : (
              <button type="button" className="cv-btn" onClick={startRecording} data-testid="conversation-record">
                Record
              </button>
            )}
            <button
              type="button"
              className="cv-btn cv-btn--primary"
              disabled={busy || recording || !transcript.trim()}
              onClick={analyze}
              data-testid="conversation-analyze"
            >
              Analyze
            </button>
          </div>
          <SpeechDebugPanel debug={dictation.debug} />
          <label className="cv-label" htmlFor="conversation-transcript">
            Transcript
          </label>
          <textarea
            id="conversation-transcript"
            className="cv-transcript"
            value={transcript}
            onChange={(event) => {
              dictation.takeOver();
              setTranscript(event.target.value);
            }}
            placeholder="Speech transcription fills this in. You can also type what was said."
            data-testid="conversation-transcript"
          />
          <p className="cv-hint">
            Dates such as Friday or tomorrow are read from today. A person is assigned only when one member has that
            name. Priority and location stay blank unless the words support them.
          </p>
        </section>
        {notes.map((note) => (
          <p key={note} className="cv-note">
            {note}
          </p>
        ))}
        {proposed.length > 1 && mayApprove ? (
          <button
            type="button"
            className="cv-btn cv-btn--primary cv-btn--block"
            disabled={busy}
            onClick={approveAll}
            data-testid="conversation-approve-all"
          >
            Approve all
          </button>
        ) : null}
        <ul className="cv-list">
          {rows.map((row) => (
            <li key={row.recommendation_id} className="rec-card cv-item" data-testid="conversation-recommendation">
              <div className="cv-item__status">{row.status}</div>
              <h2 className="cv-item__title">{row.title}</h2>
              <p className="cv-item__evidence">{row.evidence}</p>
              <p className="cv-item__meta">
                {[
                  row.assignee_id ? "Assignee matched" : "Unassigned",
                  row.due_date || "No due date",
                  row.location_id ? "Location matched" : "No location",
                  row.priority || "No priority",
                ].join(" · ")}
              </p>
              {row.inference_notes ? <p className="cv-hint">{row.inference_notes}</p> : null}
              {row.status === "proposed" && mayApprove ? (
                <div className="cv-actions">
                  <button type="button" className="cv-btn cv-btn--primary" disabled={busy} onClick={() => approve(row)}>
                    Approve
                  </button>
                  <button
                    type="button"
                    className="cv-btn"
                    disabled={busy}
                    onClick={() => {
                      setEditingId(row.recommendation_id);
                      setDraft({
                        title: row.title,
                        description: row.description || "",
                        dueDate: row.due_date || "",
                        priority: row.priority || "",
                      });
                    }}
                  >
                    Edit & approve
                  </button>
                  <button type="button" className="cv-btn" disabled={busy} onClick={() => dismiss(row)}>
                    Dismiss
                  </button>
                </div>
              ) : null}
              {editingId === row.recommendation_id && draft ? (
                <form
                  className="cv-edit"
                  onSubmit={(event) => {
                    event.preventDefault();
                    approve(row, draft);
                  }}
                >
                  <label className="cv-label" htmlFor={`cv-title-${row.recommendation_id}`}>
                    Title
                  </label>
                  <input
                    id={`cv-title-${row.recommendation_id}`}
                    className="cv-input"
                    value={draft.title}
                    onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                  />
                  <label className="cv-label" htmlFor={`cv-due-${row.recommendation_id}`}>
                    Due date
                  </label>
                  <input
                    id={`cv-due-${row.recommendation_id}`}
                    className="cv-input"
                    type="date"
                    value={draft.dueDate}
                    onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })}
                  />
                  <div className="cv-actions">
                    <button type="submit" className="cv-btn cv-btn--primary" disabled={busy}>
                      Save and approve
                    </button>
                    <button type="button" className="cv-btn" onClick={() => setEditingId("")}>
                      Cancel
                    </button>
                  </div>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function editsToRow(edits = {}) {
  return {
    ...(edits.title !== undefined ? { title: edits.title } : {}),
    ...(edits.dueDate !== undefined ? { due_date: edits.dueDate || null } : {}),
    ...(edits.priority !== undefined ? { priority: edits.priority || null } : {}),
  };
}
